import { z } from 'zod';

// Enum values shared by the DB schema, REST API, MCP tools, and UI.
export const USER_ROLES = ['admin', 'member'] as const;
export const SECTION_LAYOUTS = ['tiles', 'list'] as const;
export const LINK_OPEN_IN = ['default', 'same_tab', 'new_tab'] as const;
export const LINK_SOURCES = ['ui', 'mcp', 'import', 'discovery'] as const;
export const ICON_KINDS = ['mdi', 'url', 'favicon', 'upload'] as const;

export type UserRole = (typeof USER_ROLES)[number];
export type SectionLayout = (typeof SECTION_LAYOUTS)[number];
export type LinkOpenIn = (typeof LINK_OPEN_IN)[number];
export type LinkSource = (typeof LINK_SOURCES)[number];

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_.-]{2,32}$/, '2-32 characters: a-z, 0-9, _ . -');
export const displayNameSchema = z.string().trim().min(1).max(64);
export const passkeyNameSchema = z.string().trim().min(1).max(64);

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  role: UserRole;
}

export const linkIconSchema = z.object({
  kind: z.enum(ICON_KINDS),
  value: z.string().max(2048),
});
export type LinkIcon = z.infer<typeof linkIconSchema>;

export const searchProviderSchema = z.object({
  name: z.string().min(1).max(64),
  prefix: z.string().regex(/^[a-z0-9]{1,8}$/i),
  // `{q}` is replaced with the encoded query; if absent, the query is appended.
  template: z.string().url().max(2048),
});
export type SearchProvider = z.infer<typeof searchProviderSchema>;

// Per-user settings stored as JSONB. Every field has a default so that stored
// documents written by older versions parse into the current shape.
export const userSettingsSchema = z.object({
  appearance: z
    .object({
      mode: z.enum(['system', 'light', 'dark']).default('system'),
      theme: z.string().max(64).default('tron'),
      accent: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .nullable()
        .default(null),
      customCss: z.string().max(50_000).default(''),
    })
    .prefault({}),
  layout: z
    .object({
      columns: z.number().int().min(1).max(8).default(4),
      density: z.enum(['comfortable', 'compact']).default('comfortable'),
      showSearch: z.boolean().default(true),
      showClock: z.boolean().default(true),
    })
    .prefault({}),
  search: z
    .object({
      defaultPrefix: z.string().max(8).default('l'),
      secondaryPrefix: z.string().max(8).default('d'),
      providers: z.array(searchProviderSchema).max(50).default([]),
    })
    .prefault({}),
  links: z
    .object({
      openIn: z.enum(['same_tab', 'new_tab']).default('new_tab'),
    })
    .prefault({}),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;

export const defaultUserSettings = (): UserSettings =>
  userSettingsSchema.parse({});
